/**
 * API Error Message Extraction
 *
 * The backend answers failures in more than one shape: FastAPI's `{ detail }`
 * (string, object, or a 422 validation array) and the project envelope
 * `{ success, data, error, message }` (CLAUDE.md, API conventions). Call sites
 * that read only one of them silently fall back to a generic string, which is
 * how a 500 reached the admin UI as a non-save that only DevTools explained
 * (REQ-003, FE-3-1).
 *
 * Returns **plain text**, never markup: every consumer renders it through antd
 * `message.error()` / `<Alert>` text, so React escaping applies. Never pass the
 * result to `dangerouslySetInnerHTML` — a server error detail is
 * attacker-influencable text (SEC-1-1).
 *
 * Usage:
 *   import { getApiErrorMessage } from '../../utils/apiError';
 *   message.error(getApiErrorMessage(error, 'Failed to create user.'));
 */

// Long enough for a real validation message, short enough for a toast.
const MAX_DETAIL_LENGTH = 400;

/**
 * The body the backend's global exception handler returns for any unhandled
 * error (BE-5-10). It says nothing a user can act on, so it is never shown as
 * the message (FE-5-18).
 */
export const GENERIC_SERVER_ERROR_DETAIL = 'Internal server error';

/** What end users see instead of a 500 (the wording these pages used before BE-5-10). */
export const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

/**
 * @param {*} detail - A response `detail`
 * @returns {boolean} true if it is the backend's generic 500 body
 */
export const isGenericServerDetail = (detail) =>
  typeof detail === 'string' && detail.trim() === GENERIC_SERVER_ERROR_DETAIL;

/**
 * For end-user screens: a 500, or the generic 500 detail under any status, maps
 * to UNEXPECTED_ERROR_MESSAGE. Returns null otherwise, so the caller keeps its
 * own handling (e.g. a curated 502 or 422 detail is still shown).
 *
 * @param {number} [status] - HTTP status
 * @param {*} [detail] - Response `detail`
 * @returns {string|null}
 */
export const getUserFacingServerError = (status, detail) =>
  (status === 500 || isGenericServerDetail(detail) ? UNEXPECTED_ERROR_MESSAGE : null);

/**
 * Collapse whitespace and strip control characters so a multi-line traceback
 * cannot break the toast layout.
 *
 * @param {string} text - Raw text from the API
 * @returns {string} Single-line, trimmed, length-capped text
 */
const toSingleLine = (text) => {
  // eslint-disable-next-line no-control-regex
  const cleaned = String(text).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.length > MAX_DETAIL_LENGTH
    ? `${cleaned.slice(0, MAX_DETAIL_LENGTH - 1)}…`
    : cleaned;
};

/**
 * Render one FastAPI 422 validation item as "field: message".
 *
 * @param {Object} item - A `detail[]` entry: `{ loc, msg, type }`
 * @returns {string} Readable field-scoped message
 */
const formatValidationItem = (item) => {
  if (!item || typeof item !== 'object') return toSingleLine(item);
  // `loc` is like ['body', 'email']; the leading segment is the request part.
  const field = Array.isArray(item.loc) ? item.loc.slice(1).join('.') || item.loc.join('.') : '';
  const msg = item.msg || item.message || item.type || '';
  return field ? `${field}: ${msg}` : String(msg);
};

/**
 * Pull the most specific human-readable string out of a response body.
 *
 * @param {*} data - Parsed response body
 * @returns {string} Extracted detail, or '' when the body carries none
 */
const extractDetail = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return toSingleLine(data);

  const { detail } = data;

  if (typeof detail === 'string' && detail.trim()) return toSingleLine(detail);

  // FastAPI 422: detail is an array of validation items.
  if (Array.isArray(detail) && detail.length > 0) {
    return toSingleLine(detail.map(formatValidationItem).filter(Boolean).join('; '));
  }

  // FastAPI structured detail: { error, message, ...context }
  if (detail && typeof detail === 'object') {
    const nested = detail.message || detail.error || detail.detail;
    if (typeof nested === 'string' && nested.trim()) return toSingleLine(nested);
  }

  // Project envelope: { success, data, error, message }
  if (typeof data.message === 'string' && data.message.trim()) return toSingleLine(data.message);
  if (typeof data.error === 'string' && data.error.trim()) return toSingleLine(data.error);
  if (data.error && typeof data.error === 'object') {
    const nested = data.error.message || data.error.detail;
    if (typeof nested === 'string' && nested.trim()) return toSingleLine(nested);
  }

  return '';
};

/**
 * Build the operator-facing message for a failed API call.
 *
 * @param {Error} error - The thrown error (axios error, or any Error)
 * @param {string} [fallback] - Message to use when the response carries no detail
 * @returns {string} Plain-text message, suffixed with the HTTP status when there was a response
 */
export const getApiErrorMessage = (error, fallback = 'The request failed. Please try again.') => {
  const response = error?.response;

  // No response at all: network failure, CORS, or timeout.
  if (!response) {
    if (error?.code === 'ECONNABORTED') {
      return 'The request timed out before the server answered. Please try again.';
    }
    return error?.message
      ? `Could not reach the server: ${toSingleLine(error.message)}`
      : fallback;
  }

  const status = response.status;
  // A specific 500 detail stays visible to operators (FE-3-1); only the
  // backend's generic body is dropped so the call site's fallback shows (FE-5-18).
  const extracted = extractDetail(response.data);
  const detail = isGenericServerDetail(extracted) ? '' : extracted;

  // A 401/403 is about the operator's own session, not the record being saved.
  if (!detail && status === 401) {
    return 'Your session has expired. Please log in again.';
  }
  if (!detail && status === 403) {
    return 'You do not have permission to perform this action.';
  }

  const body = detail || fallback;
  return status ? `${body} (HTTP ${status})` : body;
};

export default getApiErrorMessage;
